import React from "react";
import {
  type LogEntry,
  getActionLabel,
  getEntityLabel,
  formatDetailsReadable,
  getReadableDescription,
  formatDetails,
  userName,
} from "./logsShared";

interface LogsTableProps {
  logs: LogEntry[];
  /** Clicking a patient name narrows the list to that patient. */
  onPatientClick?: (patient: { id: string; name: string }) => void;
}

const TH = "px-3 py-2 text-left text-xs font-semibold text-primary dark:text-slate-200 uppercase";
const TD_MUTED = "px-3 py-2 text-xs text-slate-600 dark:text-slate-400";

/** The log table shared by All Logs and My Logs so the two never drift. */
function LogsTable({ logs, onPatientClick }: LogsTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-primary/5 dark:bg-slate-700/50 border-b border-primary/10 dark:border-slate-600">
          <tr>
            <th className={`${TH} w-[140px]`}>Time</th>
            <th className={`${TH} w-[130px]`} title="Staff who made this change">Who made this change</th>
            <th className={`${TH} w-[150px]`}>Patient</th>
            <th className={`${TH} w-[110px]`}>Reg No</th>
            <th className={TH}>What happened</th>
            <th className={`${TH} w-[100px]`}>Action</th>
            <th className={`${TH} w-[90px]`}>Entity</th>
            <th className={`${TH} w-[140px]`}>Subject / details</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
          {logs.map((entry) => {
            const patientName = entry.patientName?.trim() || "";
            const canFilter = Boolean(entry.patientId && patientName && onPatientClick);
            return (
              <tr key={entry._id} className="hover:bg-primary/5 dark:hover:bg-slate-700/50">
                <td className={`${TD_MUTED} whitespace-nowrap`}>
                  {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : "—"}
                </td>
                <td className="px-3 py-2 text-xs text-slate-700 dark:text-slate-300 font-medium" title="Staff who made this change">
                  {userName(entry)}
                </td>
                <td className="px-3 py-2 text-xs text-slate-700 dark:text-slate-300">
                  {canFilter ? (
                    <button
                      type="button"
                      className="text-left font-medium text-primary dark:text-slate-200 hover:underline"
                      title="Show only this patient's logs"
                      onClick={() => onPatientClick?.({ id: entry.patientId as string, name: patientName })}
                    >
                      {patientName}
                    </button>
                  ) : (
                    patientName || "—"
                  )}
                </td>
                <td className={`${TD_MUTED} whitespace-nowrap font-mono`}>{entry.regNo?.trim() || "—"}</td>
                <td className="px-3 py-2 text-sm text-slate-800 dark:text-slate-200" title={getReadableDescription(entry)}>
                  {getReadableDescription(entry)}
                </td>
                <td className={TD_MUTED}>{getActionLabel(entry.action || "")}</td>
                <td className={TD_MUTED}>{getEntityLabel(entry.entity || "")}</td>
                <td className={`${TD_MUTED} max-w-[220px]`} title={formatDetailsReadable(entry.details)}>
                  {formatDetails(entry.details)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default LogsTable;
